import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, Eye, Check, X, Link2, Plus } from 'lucide-react';

const LANGUAGE_OPTIONS = [
  { value: '1', label: '🇸🇪 Svenska' },
  { value: '2', label: '🇬🇧 Engelska' },
  { value: '4', label: '🇩🇪 Tyska' },
  { value: '8', label: '🇫🇷 Franska' },
  { value: '16', label: '🇪🇸 Spanska' },
];

interface ImportResult {
  wordCreated: boolean;
  wordLanguageUpdated: boolean;
  cluesImported: number;
  cluesSkipped: number;
  reverseLinksCreated: number;
  wordsAdded: number;
  errors: string[];
}

interface PendingClue {
  clue: string;
  clueUpper: string;
}

interface PendingReverseLink {
  fromWord: string;        // T.ex. "VOVVE"
  fromWordId?: number;     // Om ordet redan finns
  toWord: string;          // T.ex. "HUND" (huvudordet)
  fromWordAction: 'create' | 'exists' | 'update_language';
}

interface PendingWord {
  word: string;
  wordId?: number;
  action: 'create' | 'exists' | 'update_language';
}

interface PendingImport {
  mainWord: {
    wordUpper: string;
    wordId?: number;
    action: 'create' | 'update_language' | 'exists';
  };
  cluesToCreate: PendingClue[];
  cluesToSkip: string[];
  reverseLinks: PendingReverseLink[];
  wordsToAdd: PendingWord[];
  selectedLanguage: number;
}

export function ClueImportAdmin() {
  const [mainWord, setMainWord] = useState('');
  const [clueList, setClueList] = useState('');
  const [clueLanguage, setClueLanguage] = useState('1');
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Validera session och admin-behörighet
  const validateSession = async (): Promise<boolean> => {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      toast.error('Du är inte inloggad. Logga in och försök igen.');
      return false;
    }
    
    // Kontrollera att användaren fortfarande är admin
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .maybeSingle();
    
    if (!roles) {
      toast.error('Du har inte admin-behörighet.');
      return false;
    }
    
    return true;
  };

  const parseClues = (text: string): string[] => {
    return text
      .split(/\n/)
      .map(line => line.trim())
      .filter(line => line.length > 0 && line.length <= 200);
  };

  const normalizeWord = (word: string): string => {
    return word.trim().toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
  };

  // Förhandsgranskningsfas - analyserar utan att spara
  const analyzeImport = async () => {
    // Validera session först
    if (!await validateSession()) {
      return;
    }

    const wordUpper = normalizeWord(mainWord);
    const clues = parseClues(clueList);
    const selectedLanguage = parseInt(clueLanguage);

    if (!wordUpper || wordUpper.length < 1) {
      toast.error('Ange ett giltigt huvudord (minst 1 bokstav).');
      return;
    }

    if (clues.length === 0) {
      toast.error('Ange minst en ledtråd.');
      return;
    }

    setAnalyzing(true);
    setImportResult(null);

    try {
      const pending: PendingImport = {
        mainWord: {
          wordUpper,
          action: 'exists'
        },
        cluesToCreate: [],
        cluesToSkip: [],
        reverseLinks: [],
        wordsToAdd: [],
        selectedLanguage
      };

      // 1. Kontrollera om huvudordet finns
      // Använd limit(1) istället för maybeSingle() för att hantera eventuella dubbletter
      const { data: existingWords } = await supabase
        .from('words')
        .select('id, language')
        .eq('word_upper', wordUpper)
        .limit(1);
      const existingWord = existingWords?.[0] ?? null;

      if (!existingWord) {
        pending.mainWord.action = 'create';
      } else {
        pending.mainWord.wordId = existingWord.id;
        const currentLanguage = existingWord.language || 1;
        if ((currentLanguage & selectedLanguage) === 0) {
          pending.mainWord.action = 'update_language';
        }
      }

      // 2. Hämta befintliga ledtrådar för huvudordet
      let existingClueSet = new Set<string>();
      if (existingWord) {
        const { data: existingClues } = await supabase
          .from('clues')
          .select('clue_upper')
          .eq('word_id', existingWord.id);
        existingClueSet = new Set((existingClues || []).map(c => c.clue_upper));
      }

      // 3. Analysera varje ledtråd
      for (const clue of clues) {
        const clueUpper = clue.toUpperCase();

        // Hoppa över om ledtråden redan finns
        if (existingClueSet.has(clueUpper)) {
          pending.cluesToSkip.push(clue);
          continue;
        }

        // Lägg till ledtråden
        pending.cluesToCreate.push({ clue, clueUpper });

        // Analysera ord i ledtråden
        const clueWords = clue.trim().split(/\s+/).filter(w => w.length >= 2);
        const isSingleWordClue = clueWords.length === 1;

        for (const clueWord of clueWords) {
          const clueWordUpper = clueWord.toUpperCase().replace(/[^A-ZÅÄÖ]/g, '');
          if (!clueWordUpper || clueWordUpper.length < 2) continue;
          if (clueWordUpper === wordUpper) continue; // Hoppa över om samma som huvudordet

          // Kontrollera om ledtrådens ord finns i databasen
          // Använd limit(1) istället för maybeSingle() för att hantera eventuella dubbletter
          const { data: clueWordEntries } = await supabase
            .from('words')
            .select('id, language')
            .eq('word_upper', clueWordUpper)
            .limit(1);
          const clueWordEntry = clueWordEntries?.[0] ?? null;

          let wordAction: 'create' | 'exists' | 'update_language' = 'create';
          let wordId: number | undefined;

          if (clueWordEntry) {
            wordId = clueWordEntry.id;
            const currentLang = clueWordEntry.language || 1;
            if ((currentLang & selectedLanguage) === 0) {
              wordAction = 'update_language';
            } else {
              wordAction = 'exists';
            }
          }

          // Undvik dubbletter i wordsToAdd
          const alreadyAdded = pending.wordsToAdd.some(w => w.word === clueWordUpper);
          if (!alreadyAdded) {
            pending.wordsToAdd.push({
              word: clueWordUpper,
              wordId,
              action: wordAction
            });
          }

          // ENDAST för enordssynonymer: Förbered omvänd länk
          if (isSingleWordClue) {
            // Kontrollera om omvänd länk redan finns
            let reverseExists = false;
            if (clueWordEntry) {
              const { data: existingReverse } = await supabase
                .from('clues')
                .select('id')
                .eq('word_id', clueWordEntry.id)
                .eq('clue_upper', wordUpper)
                .maybeSingle();
              reverseExists = !!existingReverse;
            }

            if (!reverseExists) {
              // Undvik dubbletter i reverseLinks
              const alreadyLinked = pending.reverseLinks.some(
                r => r.fromWord === clueWordUpper && r.toWord === wordUpper
              );
              if (!alreadyLinked) {
                pending.reverseLinks.push({
                  fromWord: clueWordUpper,
                  fromWordId: wordId,
                  toWord: wordUpper,
                  fromWordAction: wordAction
                });
              }
            }
          }
        }
      }

      setPendingImport(pending);
      setShowConfirmDialog(true);
    } catch (error) {
      toast.error('Ett fel uppstod vid analysen.');
      console.error('Analyze error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  // Sparfas - körs endast efter bekräftelse
  const handleImport = async () => {
    if (!pendingImport) return;

    // Validera session igen innan import
    if (!await validateSession()) {
      setShowConfirmDialog(false);
      return;
    }

    setShowConfirmDialog(false);
    setImporting(true);

    try {
      const result: ImportResult = {
        wordCreated: false,
        wordLanguageUpdated: false,
        cluesImported: 0,
        cluesSkipped: pendingImport.cluesToSkip.length,
        reverseLinksCreated: 0,
        wordsAdded: 0,
        errors: []
      };

      const { selectedLanguage } = pendingImport;
      let mainWordId = pendingImport.mainWord.wordId;

      // 1. Skapa eller uppdatera huvudordet
      if (pendingImport.mainWord.action === 'create') {
        const { data: newWord, error: wordError } = await supabase
          .from('words')
          .insert({
            word: pendingImport.mainWord.wordUpper.charAt(0) + 
                  pendingImport.mainWord.wordUpper.slice(1).toLowerCase(),
            word_upper: pendingImport.mainWord.wordUpper,
            length: pendingImport.mainWord.wordUpper.length,
            language: selectedLanguage
          })
          .select('id')
          .single();

        if (wordError) {
          toast.error(`Kunde inte skapa ordet: ${wordError.message}`);
          setImporting(false);
          return;
        }

        mainWordId = newWord.id;
        result.wordCreated = true;
      } else if (pendingImport.mainWord.action === 'update_language' && mainWordId) {
        const { data: existingWord } = await supabase
          .from('words')
          .select('language')
          .eq('id', mainWordId)
          .single();
        
        if (existingWord) {
          await supabase
            .from('words')
            .update({ language: (existingWord.language || 1) | selectedLanguage })
            .eq('id', mainWordId);
          result.wordLanguageUpdated = true;
        }
      }

      if (!mainWordId) {
        toast.error('Kunde inte hitta eller skapa huvudordet.');
        setImporting(false);
        return;
      }

      // 2. Skapa ledtrådar
      for (const clue of pendingImport.cluesToCreate) {
        const { error: clueError } = await supabase
          .from('clues')
          .insert({
            word_id: mainWordId,
            clue: clue.clue,
            clue_upper: clue.clueUpper,
            language: selectedLanguage
          });

        if (clueError) {
          if (clueError.message.includes('row-level security')) {
            result.errors.push(`Ledtråd "${clue.clue}": Behörighetsproblem - din session kan ha löpt ut`);
          } else {
            result.errors.push(`Ledtråd "${clue.clue}": ${clueError.message}`);
          }
        } else {
          result.cluesImported++;
        }
      }

      // 3. Skapa eller uppdatera ord från ledtrådarna
      const wordIdMap = new Map<string, number>();
      
      for (const wordToAdd of pendingImport.wordsToAdd) {
        if (wordToAdd.action === 'create') {
          const { data: newWord, error } = await supabase
            .from('words')
            .insert({
              word: wordToAdd.word.charAt(0) + wordToAdd.word.slice(1).toLowerCase(),
              word_upper: wordToAdd.word,
              length: wordToAdd.word.length,
              language: selectedLanguage
            })
            .select('id')
            .single();

          if (!error && newWord) {
            wordIdMap.set(wordToAdd.word, newWord.id);
            result.wordsAdded++;
          } else if (error) {
            result.errors.push(`Ord "${wordToAdd.word}": ${error.message}`);
          }
        } else if (wordToAdd.action === 'update_language' && wordToAdd.wordId) {
          const { data: existingWord } = await supabase
            .from('words')
            .select('language')
            .eq('id', wordToAdd.wordId)
            .single();
          
          if (existingWord) {
            await supabase
              .from('words')
              .update({ language: (existingWord.language || 1) | selectedLanguage })
              .eq('id', wordToAdd.wordId);
          }
          wordIdMap.set(wordToAdd.word, wordToAdd.wordId);
        } else if (wordToAdd.wordId) {
          wordIdMap.set(wordToAdd.word, wordToAdd.wordId);
        }
      }

      // 4. Skapa omvända länkar för enordssynonymer
      for (const reverseLink of pendingImport.reverseLinks) {
        const fromWordId = reverseLink.fromWordId || wordIdMap.get(reverseLink.fromWord);
        
        if (!fromWordId) {
          result.errors.push(`Omvänd länk: Kunde inte hitta ord "${reverseLink.fromWord}"`);
          continue;
        }

        const { error: reverseLinkError } = await supabase
          .from('clues')
          .insert({
            word_id: fromWordId,
            clue: reverseLink.toWord.charAt(0) + reverseLink.toWord.slice(1).toLowerCase(),
            clue_upper: reverseLink.toWord,
            language: selectedLanguage
          });

        if (reverseLinkError) {
          result.errors.push(`Omvänd länk ${reverseLink.fromWord}→${reverseLink.toWord}: ${reverseLinkError.message}`);
        } else {
          result.reverseLinksCreated++;
        }
      }

      setImportResult(result);

      if (result.cluesImported > 0 || result.reverseLinksCreated > 0) {
        toast.success(
          `${result.cluesImported} ledtrådar och ${result.reverseLinksCreated} omvända länkar skapades!`
        );
        setMainWord('');
        setClueList('');
      } else if (result.cluesSkipped === pendingImport.cluesToCreate.length + pendingImport.cluesToSkip.length) {
        toast.info('Alla ledtrådar finns redan.');
      }

      setPendingImport(null);
    } catch (error) {
      toast.error('Ett fel uppstod vid importen.');
      console.error('Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setClueList(text);
    };
    reader.readAsText(file);
  };

  const clueCount = parseClues(clueList).length;
  const wordValid = normalizeWord(mainWord).length >= 1;

  const getActionLabel = (action: 'create' | 'exists' | 'update_language') => {
    switch (action) {
      case 'create': return 'skapas';
      case 'update_language': return 'uppdateras (språk)';
      case 'exists': return 'finns';
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-display">Importera ledtrådar till ord</CardTitle>
          <CardDescription>
            Ange ett huvudord och klistra in ledtrådar/synonymer separerade med radbrytning.
            <br />
            <strong>Enordssynonymer</strong> länkas i båda riktningar. <strong>Flerordsfraser</strong> läggs endast till som ledtråd.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mainWord">Huvudord</Label>
            <Input
              id="mainWord"
              value={mainWord}
              onChange={(e) => setMainWord(e.target.value)}
              placeholder="T.ex. HUND"
              className="font-mono uppercase"
            />
            {mainWord && !wordValid && (
              <p className="text-sm text-destructive">
                Ordet måste vara minst 1 bokstav (A-Ö)
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="clueLanguage">Språk för ledtrådar</Label>
            <Select value={clueLanguage} onValueChange={setClueLanguage}>
              <SelectTrigger id="clueLanguage" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="clueList">Ledtrådar (en per rad)</Label>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button variant="outline" size="sm" asChild>
                  <span className="gap-2">
                    <FileText className="h-4 w-4" />
                    Ladda upp .txt
                  </span>
                </Button>
              </label>
            </div>
            <Textarea
              id="clueList"
              value={clueList}
              onChange={(e) => setClueList(e.target.value)}
              placeholder="VOVVE&#10;FYRFOTA DJUR&#10;MANS BÄSTA VÄN"
              rows={10}
              className="font-mono"
            />
            <p className="text-sm text-muted-foreground">
              {clueCount} ledtrådar identifierade
            </p>
          </div>

          <Button
            onClick={analyzeImport}
            disabled={analyzing || importing || clueCount === 0 || !wordValid}
            className="gap-2"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyserar...
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Förhandsgranska import
              </>
            )}
          </Button>

          {importResult && (
            <div className="mt-4 p-4 rounded-lg bg-muted space-y-2">
              <p className="font-medium">Resultat:</p>
              <ul className="text-sm space-y-1">
                {importResult.wordCreated && (
                  <li className="text-blue-600 dark:text-blue-400">
                    + Ordet "{normalizeWord(mainWord)}" skapades
                  </li>
                )}
                {importResult.wordLanguageUpdated && (
                  <li className="text-blue-600 dark:text-blue-400">
                    + Språk lades till på huvudordet
                  </li>
                )}
                <li className="text-green-600 dark:text-green-400">
                  ✓ {importResult.cluesImported} ledtrådar importerade
                </li>
                {importResult.reverseLinksCreated > 0 && (
                  <li className="text-green-600 dark:text-green-400">
                    ✓ {importResult.reverseLinksCreated} omvända länkar skapades
                  </li>
                )}
                {importResult.wordsAdded > 0 && (
                  <li className="text-blue-600 dark:text-blue-400">
                    + {importResult.wordsAdded} nya ord lades till
                  </li>
                )}
                {importResult.cluesSkipped > 0 && (
                  <li className="text-muted-foreground">
                    ○ {importResult.cluesSkipped} ledtrådar fanns redan
                  </li>
                )}
                {importResult.errors.length > 0 && (
                  <li className="text-destructive">
                    ✕ {importResult.errors.length} fel
                  </li>
                )}
              </ul>
              {importResult.errors.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">
                    Visa felmeddelanden
                  </summary>
                  <ul className="mt-2 space-y-1 text-destructive">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bekräftelsedialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Bekräfta import</AlertDialogTitle>
            <AlertDialogDescription>
              Granska ändringarna innan du bekräftar.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {pendingImport && (
            <div className="space-y-4 py-2">
              {/* Sammanfattning först - tydlig överblick */}
              <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
                <h4 className="font-semibold text-base mb-3">Sammanfattning</h4>
                <ul className="space-y-2 text-sm">
                  {/* Huvudord */}
                  <li className="flex items-center gap-2">
                    <span className={pendingImport.mainWord.action === 'create' 
                      ? 'text-green-600 dark:text-green-400 font-medium' 
                      : 'text-muted-foreground'}>
                      {pendingImport.mainWord.action === 'create' ? '+ 1 huvudord skapas' :
                       pendingImport.mainWord.action === 'update_language' ? '↻ Huvudord uppdateras (språk)' :
                       '• Huvudord finns redan'}
                    </span>
                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                      {pendingImport.mainWord.wordUpper}
                    </span>
                  </li>
                  
                  {/* Ledtrådar */}
                  <li className={pendingImport.cluesToCreate.length > 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-muted-foreground'}>
                    {pendingImport.cluesToCreate.length > 0 
                      ? `+ ${pendingImport.cluesToCreate.length} ledtråd${pendingImport.cluesToCreate.length > 1 ? 'ar' : ''} skapas`
                      : '• Inga nya ledtrådar'}
                  </li>
                  
                  {/* Omvända länkar */}
                  {pendingImport.reverseLinks.length > 0 && (
                    <li className="text-purple-600 dark:text-purple-400">
                      ↔ {pendingImport.reverseLinks.length} omvänd{pendingImport.reverseLinks.length > 1 ? 'a' : ''} länk{pendingImport.reverseLinks.length > 1 ? 'ar' : ''} skapas
                    </li>
                  )}
                  
                  {/* Nya ord */}
                  {pendingImport.wordsToAdd.filter(w => w.action === 'create').length > 0 && (
                    <li className="text-blue-600 dark:text-blue-400">
                      + {pendingImport.wordsToAdd.filter(w => w.action === 'create').length} nya ord läggs till
                    </li>
                  )}
                  
                  {/* Hoppas över */}
                  {pendingImport.cluesToSkip.length > 0 && (
                    <li className="text-muted-foreground text-xs">
                      ({pendingImport.cluesToSkip.length} ledtråd{pendingImport.cluesToSkip.length > 1 ? 'ar' : ''} finns redan)
                    </li>
                  )}
                </ul>
              </div>

              {/* Detaljer i kollapsbar sektion */}
              <details className="group">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                  <span className="group-open:rotate-90 transition-transform">▶</span>
                  Visa detaljer
                </summary>
                
                <div className="mt-3 space-y-3 pl-4 border-l-2 border-muted">
                  {/* Ledtrådar som skapas */}
                  {pendingImport.cluesToCreate.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Ledtrådar:</p>
                      <ul className="text-sm space-y-0.5 max-h-24 overflow-y-auto">
                        {pendingImport.cluesToCreate.map((clue, i) => (
                          <li key={i} className="text-green-700 dark:text-green-400 text-xs">
                            → "{clue.clue}"
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Omvända länkar */}
                  {pendingImport.reverseLinks.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Omvända länkar:</p>
                      <ul className="text-sm space-y-0.5 max-h-24 overflow-y-auto">
                        {pendingImport.reverseLinks.map((link, i) => (
                          <li key={i} className="text-purple-700 dark:text-purple-400 text-xs">
                            {link.fromWord} → {link.toWord}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Ord från ledtrådar */}
                  {pendingImport.wordsToAdd.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Ord från ledtrådar:</p>
                      <ul className="text-sm space-y-0.5 max-h-24 overflow-y-auto">
                        {pendingImport.wordsToAdd.map((word, i) => (
                          <li key={i} className={`text-xs ${
                            word.action === 'create' 
                              ? 'text-blue-700 dark:text-blue-400' 
                              : 'text-muted-foreground'
                          }`}>
                            {word.word} ({getActionLabel(word.action)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Hoppas över */}
                  {pendingImport.cluesToSkip.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Hoppas över:</p>
                      <ul className="text-xs text-muted-foreground max-h-16 overflow-y-auto">
                        {pendingImport.cluesToSkip.map((clue, i) => (
                          <li key={i}>"{clue}"</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleImport} disabled={importing}>
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Importerar...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importera
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
